import React, { useEffect, useState } from "react";
import { ArrowDownRight, ArrowUpRight, Check, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { DeploySignal, Deployment } from "../../../types/strategyDeploy";
import { SignalChart } from "./SignalChart";
import { liveCardClassName, liveFigureClassName, liveLabelClassName, liveSmallTextClassName } from "./liveStrategiesUi";
import { LiveBadge, type LiveTone } from "./LiveUiParts";
import {
  GREEN,
  RED,
  SIGNAL_STATUS_LABEL,
  SURF,
  countdown,
  fmtMoney,
  fmtPrice,
  fmtR,
  rewardRisk,
  signColor,
  timeAgo,
} from "./liveStrategiesFormat";

type Props = {
  signal: DeploySignal;
  deployment?: Deployment | null;
  onDecide?: (signal: DeploySignal, decision: "approve" | "reject") => Promise<void>;
  compact?: boolean;
};

function Level({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className={liveLabelClassName}>{label}</span>
      <span className={cn(liveFigureClassName, "truncate")} style={color ? { color } : undefined}>
        {value}
      </span>
    </div>
  );
}

function signalTone(signal: DeploySignal): LiveTone {
  if (signal.status === "pending" || signal.status === "open" || signal.status === "paper") return "brand";
  if (signal.status === "submitted" || signal.status === "approved") return "brand";
  if (signal.status === "failed") return "bad";
  if (signal.status === "closed") {
    const v = signal.r_multiple ?? signal.pnl ?? 0;
    return v > 0 ? "good" : v < 0 ? "bad" : "neutral";
  }
  if (signal.status === "expired") return "warn";
  return "neutral";
}

export function SignalStatusPill({ signal }: { signal: DeploySignal }) {
  const live = signal.status === "pending" || signal.status === "open" || signal.status === "paper";
  return (
    <LiveBadge tone={signalTone(signal)} pulse={live} data-live-signal-status={signal.status}>
      {SIGNAL_STATUS_LABEL[signal.status] ?? signal.status}
    </LiveBadge>
  );
}

/** A signal as a card: its chart, levels, size and, while it waits, Approve / Reject. */
export function SignalCard({ signal, deployment, onDecide, compact }: Props): JSX.Element {
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
  const [now, setNow] = useState(Date.now());
  const pending = signal.status === "pending";
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [pending]);
  const left = pending ? countdown(signal.expires_at, now) : null;
  const long = signal.direction === "long";
  const rr = rewardRisk(signal);
  const decide = async (d: "approve" | "reject") => {
    if (!onDecide) return;
    setBusy(d);
    try {
      await onDecide(signal, d);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-live-signal-card=""
      className={cn(liveCardClassName, "gap-4 p-5", pending && "!border-[color:var(--brand-tropical-surf)]")}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              "flex size-9 shrink-0 items-center justify-center rounded-[6px] text-white",
              long ? "bg-[image:var(--gradient-glacial-green)]" : "bg-[image:var(--gradient-glacial-red)]"
            )}
            aria-hidden
          >
            {long ? <ArrowUpRight className="size-4 text-white" /> : <ArrowDownRight className="size-4 text-white" />}
          </span>
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate font-[family-name:var(--font-primary)] text-base font-medium text-[color:var(--brand-tropical-surf)]">
              {long ? "Long" : "Short"} {signal.symbol}
              {deployment ? <span className="text-sm font-normal text-gray-500 dark:text-white">, {deployment.candle_type}</span> : null}
            </span>
            <span className={cn(liveSmallTextClassName, "truncate")}>
              {deployment?.name ?? "Strategy"}, {timeAgo(signal.created_at, now)}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <SignalStatusPill signal={signal} />
          {left ? (
            <span className="font-ibm-plex text-xs font-medium tabular-nums" style={{ color: SURF }} title="Time left to decide">
              {left} left
            </span>
          ) : null}
        </div>
      </div>

      {!compact ? <SignalChart signal={signal} candleType={deployment?.candle_type ?? "5m"} /> : null}

      <div className="grid grid-cols-4 gap-3">
        <Level label="Entry" value={fmtPrice(signal.fill_price ?? signal.entry_price)} />
        <Level label="Stop" value={fmtPrice(signal.stop_price)} color={RED} />
        <Level label="Target" value={fmtPrice(signal.target_price)} color={GREEN} />
        <Level label="Reward:risk" value={rr != null ? `${rr.toFixed(2)}` : "N/A"} />
      </div>
      <div className="grid grid-cols-4 gap-3">
        <Level label="Size" value={signal.quantity != null ? `${signal.quantity}` : "at approval"} />
        <Level label="Risk" value={fmtMoney(signal.risk_amount, false)} />
        {signal.status === "closed" ? (
          <>
            <Level label="Result" value={fmtR(signal.r_multiple)} color={signColor(signal.r_multiple)} />
            <Level label="P&L" value={fmtMoney(signal.pnl)} color={signColor(signal.pnl)} />
          </>
        ) : (
          <>
            <Level label="Exit" value={fmtPrice(signal.exit_price)} />
            <Level label="Decided" value={signal.decided_by ? (signal.decided_by === "system" ? "auto" : signal.decided_by) : "N/A"} />
          </>
        )}
      </div>

      {signal.status_reason && !pending ? (
        <p className={cn(liveSmallTextClassName, "m-0")}>{signal.status_reason}</p>
      ) : null}

      {pending && onDecide ? (
        <div className="grid grid-cols-2 gap-3">
          <Button
            type="button"
            variant="outline-red"
            size="sm"
            data-font="primary"
            className="h-8 shadow-none"
            disabled={busy != null}
            onClick={() => decide("reject")}
            data-live-signal-reject=""
          >
            <X className="size-3.5" />
            <span className="normal-case tracking-normal">{busy === "reject" ? "Rejecting…" : "Reject"}</span>
          </Button>
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="h-8 shadow-none"
            disabled={busy != null}
            onClick={() => decide("approve")}
            data-live-signal-approve=""
          >
            <Check className="size-3.5" />
            <span className="normal-case tracking-normal">{busy === "approve" ? "Sending…" : "Approve & trade"}</span>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
